/**
 * Landing page after a counsellor completes a Stripe checkout for a
 * 10-credit regen pack. Polls the status endpoint until payment_status
 * flips from 'paid' → 'granted' (the webhook / status-fallback grants
 * the credits) and then routes the counsellor back to their dashboard.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";


export default function CounsellorsTopUpSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ loading: true, status: null, credits_remaining: null, error: null });

  useEffect(() => {
    if (!session_id) {
      setState({ loading: false, error: "Missing session id — nothing to look up." });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/counsellors/topup/status/${session_id}`);
        setState({ loading: false, status: r.data.payment_status,
                   credits_remaining: r.data.credits_remaining, error: null });
        if (r.data.payment_status === "granted" || attempts >= 15) return;
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "Could not fetch status." });
        return;
      }
      setTimeout(poll, 3000);
    };
    poll();
  }, [session_id]);

  return (
    <div className="max-w-2xl mx-auto p-12" data-testid="counsellors-topup-success">
      <SEOHead title="Top-up complete · WUA Counsellors" description="Analysis regen credits added." path="/counsellors/topup/success" />

      {state.loading || state.status === "paid" || state.status === "pending" ? (
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto text-stone-500 animate-spin" />
          <h1 className="font-editorial text-3xl mt-4">Applying your credits…</h1>
          <p className="text-sm text-stone-600 mt-2">Takes a few seconds while Stripe confirms.</p>
        </div>
      ) : state.error ? (
        <div className="text-center" data-testid="topup-error">
          <AlertTriangle className="w-8 h-8 mx-auto text-rose-600" />
          <h1 className="font-editorial text-3xl mt-4">Something went wrong</h1>
          <p className="text-sm text-stone-600 mt-2">{state.error}</p>
          <Link
            to="/counsellors/dashboard"
            className="mt-6 inline-flex items-center gap-2 text-sm underline underline-offset-4 decoration-stone-400"
          >
            Back to dashboard
          </Link>
        </div>
      ) : (
        <div className="text-center" data-testid="topup-granted">
          <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-600" />
          <h1 className="font-editorial text-3xl mt-4">Credits added</h1>
          <p className="text-sm text-stone-600 mt-2">
            You now have <span className="font-mono">{state.credits_remaining ?? "—"}</span> analysis regens available.
          </p>
          <Link
            to="/counsellors/dashboard"
            data-testid="topup-return"
            className="mt-6 inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-5 py-3 rounded-md text-sm"
          >
            Back to dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
